/**
 * "Where should this go?" — the derived state behind the Merlin chat's
 * save dialog (proposal 8, kanban 5953f066).
 *
 * The old menu offered every open case with its title cut off, and a pick
 * attached the chat silently: a dented-drum complaint landed inside the
 * unrelated paint-coverage case INV-00004. The dialog instead offers only
 * the open cases for the SAME customer, in full, and states a one-line
 * recommendation from the data:
 *  - Merlin's proposed case file on the chat's thread (case_proposal) → a
 *    new case;
 *  - else an open case for the same customer → that case (the one that
 *    names the same lot / product / order first);
 *  - else "No open case for this customer" → a new case.
 *
 * A Merlin chat carries no structured customer, so the customer is the
 * thread's bridge_keys.customer_no when the chat is a thread's conversation,
 * otherwise the open-case customer number the conversation names most.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { CopilotTurn } from '../../pages/qualityCopilotTurns';
import type {
  DoorInvestigationRead,
  DoorThreadRead,
} from '../../services/qcpInvestigations';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import { caseWaitsOn } from '../../pages/quality-triage/caseWaitingView';
import { asksForCase } from '../../pages/quality-triage/qualityWorkspaceView';
import { isActiveCase, STATUS_LABEL } from './investigationsView';

/** What the conversation said, flattened for matching. */
export interface ConversationText {
  /** The first message a person typed (the subject line), or ''. */
  firstUserText: string;
  /** Everything said and every tool input, one string, for key matching. */
  searchText: string;
}

export function conversationTextFromTurns(turns: CopilotTurn[]): ConversationText {
  const parts: string[] = [];
  let firstUserText = '';
  for (const turn of turns) {
    if (turn.role === 'user') {
      const text = (turn.text ?? '').trim();
      if (text && !firstUserText && !turn.systemSeeded) firstUserText = text;
      parts.push(text);
      continue;
    }
    for (const block of turn.blocks ?? []) {
      if (block.kind === 'text') parts.push(block.text);
      else if (block.kind === 'tool') parts.push(JSON.stringify(block.tool.input ?? {}));
    }
  }
  return { firstUserText, searchText: parts.join('\n') };
}

export type SaveRecommendation =
  /** Merlin drafted a case file on the chat's thread: open it as a new case. */
  | { kind: 'proposal'; read: string }
  /** An open case for the same customer exists; `sameItem` when it names
   *  the same lot / product / order the conversation does. */
  | { kind: 'open_case'; investigation: DoorInvestigationRead; customerNo: string; sameItem: boolean }
  | { kind: 'no_open_case' };

export interface SaveDestination {
  /** The conversation's subject line (first message, else its title). */
  subject: string;
  /** Prefill for the new case's title. */
  proposedTitle: string;
  customerNo: string | null;
  /** Open cases for the same customer, best match first. */
  candidates: DoorInvestigationRead[];
  recommendation: SaveRecommendation;
}

export interface SaveDestinationInput {
  conversationId: string;
  conversationTitle: string | null;
  text: ConversationText;
  investigations: DoorInvestigationRead[];
  threads: DoorThreadRead[];
}

const FALLBACK_TITLE = 'Quality investigation';
// The bridge keys that name WHAT a case is about (vs who reported it).
const ITEM_KEYS = ['lot', 'lots', 'batch', 'sku', 'sku_2', 'bulk_sku', 'order_no', 'orders', 'invoice_no'];

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** How often `token` appears in `text` as a whole token (case-insensitive). */
export function countToken(text: string, token: string): number {
  const t = token.trim();
  if (t.length < 3) return 0;
  const re = new RegExp(`(?<![A-Za-z0-9])${escapeRegExp(t)}(?![A-Za-z0-9])`, 'gi');
  return text.match(re)?.length ?? 0;
}

function customerOf(inv: Pick<DoorInvestigationRead, 'bridge_keys'>): string | null {
  const c = (inv.bridge_keys?.customer_no ?? '').trim();
  return c ? c.toUpperCase() : null;
}

/** The first line of a message, without markdown noise. */
function firstLine(text: string): string {
  return (text.split('\n').find((l) => l.trim()) ?? '').trim();
}

/** How many of the case's lot / product / order keys the conversation names. */
export function itemMatchCount(inv: DoorInvestigationRead, searchText: string): number {
  let n = 0;
  for (const key of ITEM_KEYS) {
    const raw = inv.bridge_keys?.[key];
    if (!raw) continue;
    for (const value of raw.split(',')) {
      if (countToken(searchText, value) > 0) n += 1;
    }
  }
  return n;
}

export function saveDestination(input: SaveDestinationInput): SaveDestination {
  const { conversationId, conversationTitle, text, investigations, threads } = input;
  const thread = threads.find((t) => t.conversation_id === conversationId) ?? null;
  const open = investigations.filter(isActiveCase);

  let customerNo = thread ? customerOf(thread) : null;
  if (!customerNo) {
    let best = 0;
    for (const c of new Set(open.map(customerOf).filter((c): c is string => !!c))) {
      const n = countToken(text.searchText, c);
      if (n > best) {
        best = n;
        customerNo = c;
      }
    }
  }

  const candidates = customerNo
    ? open
        .filter((inv) => customerOf(inv) === customerNo)
        .map((inv) => ({ inv, score: itemMatchCount(inv, text.searchText) }))
        .sort((a, b) => b.score - a.score || b.inv.updated_at.localeCompare(a.inv.updated_at))
        .map(({ inv }) => inv)
    : [];

  const subject =
    firstLine(text.firstUserText) || (conversationTitle ?? '').trim() || (thread?.title ?? '').trim();
  const proposedTitle =
    (thread?.title ?? '').trim() || (conversationTitle ?? '').trim() || subject || FALLBACK_TITLE;

  const proposal = thread?.case_proposal ?? null;
  const proposalItems = (proposal?.items ?? []).filter((i) => i.status !== 'cancelled').length;
  let recommendation: SaveRecommendation;
  if (proposal && (proposal.current_state.trim() || proposalItems > 0)) {
    recommendation = {
      kind: 'proposal',
      read:
        proposal.current_state.trim() ||
        `he has drafted a case file with ${proposalItems} item${proposalItems === 1 ? '' : 's'} for this`,
    };
  } else if (candidates.length > 0 && customerNo) {
    recommendation = {
      kind: 'open_case',
      investigation: candidates[0],
      customerNo,
      sameItem: itemMatchCount(candidates[0], text.searchText) > 0,
    };
  } else {
    recommendation = { kind: 'no_open_case' };
  }

  return { subject, proposedTitle, customerNo, candidates, recommendation };
}

/** "Merlin's read: … Suggested: …" as one plain line. */
export function recommendationLine(rec: SaveRecommendation): string {
  if (rec.kind === 'proposal') {
    const read = rec.read.replace(/[.\s]+$/, '');
    return `Merlin's read: ${read}. Suggested: a new case.`;
  }
  if (rec.kind === 'open_case') {
    const no = rec.investigation.investigation_no;
    const where = rec.sameItem ? ' on the same product or lot' : '';
    return (
      `Merlin's read: ${rec.customerNo} already has an open case${where}, ${no}. ` +
      `Suggested: it belongs with ${no} if it is the same problem; otherwise a new case.`
    );
  }
  return "Merlin's read: No open case for this customer. Suggested: a new case.";
}

/** "Investigating · Their court · Jake Butchko · 6 open checklist items", in the
 *  Cases board's words (Our court / Their court, E2E pass 2). */
export function candidateMeta(inv: DoorInvestigationRead, asks: DoorDataRequestRead[]): string {
  const parts: string[] = [STATUS_LABEL[inv.status]];
  const names = caseWaitsOn(inv, asksForCase(asks, inv, null));
  if (names && names.length > 0) {
    parts.push(`Their court · ${names.join(', ')}`);
  } else if (names) {
    parts.push('Their court · a colleague');
  } else if (inv.work_state === 'ready_to_close') {
    parts.push('ready to close');
  } else {
    parts.push('Our court');
  }
  const openItems = inv.tasks.filter((t) => t.status === 'open').length;
  if (openItems > 0) {
    parts.push(`${openItems} open checklist item${openItems === 1 ? '' : 's'}`);
  }
  return parts.join(' · ');
}
